import { useQuery } from '@tanstack/react-query';
import { apiClient, HubspotOauthStatus, StripeOauthStatus } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

export interface ConnectionStatus {
  /** HubSpot is neither OAuth-connected nor backed by a legacy private-app token. */
  missingHubspot: boolean;
  /** Stripe is neither OAuth-connected nor backed by a legacy API key. */
  missingStripe: boolean;
  /** True once a service's status has loaded and that connection is missing. */
  hasMissingConnection: boolean;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Shared source of truth for whether the active tenant's HubSpot and Stripe
 * connections are configured. Backed by react-query so the result is cached
 * and deduped across every consumer (e.g. the app-wide connection banner and
 * anything else that needs to react to connection state). Keyed by tenant so
 * switching environments in the sidebar refetches automatically.
 */
export function useConnectionStatus(): ConnectionStatus {
  const { tenant, isAuthenticated } = useAuth();
  const enabled = isAuthenticated && !!tenant;

  const hubspotQuery = useQuery<HubspotOauthStatus>({
    queryKey: ['hubspot-oauth-status', tenant?.id],
    queryFn: () => apiClient.getHubspotOauthStatus(),
    enabled,
    staleTime: 30_000,
  });

  const stripeQuery = useQuery<StripeOauthStatus>({
    queryKey: ['stripe-oauth-status', tenant?.id],
    queryFn: () => apiClient.getStripeOauthStatus(),
    enabled,
    staleTime: 30_000,
  });

  const hubspot = hubspotQuery.data;
  const stripe = stripeQuery.data;

  const missingHubspot = hubspot ? !(hubspot.connected || hubspot.legacy_key_present) : false;
  const missingStripe = stripe ? !(stripe.connected || stripe.legacy_key_present) : false;

  // Gate each service on its own successful load so the banner surfaces a
  // genuinely-disconnected service even if the other status endpoint keeps
  // failing. `isSuccess` is false while loading (no initial-load flash) and on
  // error (no nagging when a status request fails).
  const hasMissingConnection =
    (hubspotQuery.isSuccess && missingHubspot) || (stripeQuery.isSuccess && missingStripe);

  return {
    missingHubspot,
    missingStripe,
    hasMissingConnection,
    isLoading: hubspotQuery.isLoading || stripeQuery.isLoading,
    isError: hubspotQuery.isError || stripeQuery.isError,
  };
}
